import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import uiStyle from '@/components/ui/ui.module.css';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';
import { PaymentDeviceFeeChargeStatus } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceFeeChargeDtoIn, PaymentDeviceFeesDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { paymentDeviceErrorMessage } from '@/helpers/paymentDevices';
import {
  MANAGEMENT_DEVICE_FEES_URL,
  feeChargeStatusKey,
  feeChargeTone,
  formatFeeAmount,
  formatFeeMonth,
  managementDeviceFeesUrl,
  sumFeeCharges,
} from '@/helpers/paymentDeviceFees';
import { Pill } from '@/pages/PaymentDevices/Pill';
import usePaymentDeviceApi from './usePaymentDeviceApi';

const chargeSearch: FilterFn<PaymentDeviceFeeChargeDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const query = String(value).toLowerCase();
  const { fundId, fundName, deviceNumber, aptReference, aptMovementId } = row.original;
  return [String(fundId), fundName, deviceNumber, aptReference, aptMovementId ? String(aptMovementId) : null]
    .some((field) => field?.toLowerCase().includes(query));
};

const formatTimestamp = (value: string | null) => (value ? new Date(value).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '');

/**
 * «עמלות» (HAT-1434, part 3): what the monthly run charged for a month, rental by rental, and how
 * each charge stands in APT. A charge that failed - APT was unreachable on the 1st - is retried by
 * the run every hour, and can be pushed from here at once.
 */
const FeesTab = () => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();
  const [month, setMonth] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity('table.density.paymentDeviceFees', 'normal');
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);

  // No month picked yet: the server answers with its latest month, and the picker shows that one.
  const { data, isLoading } = useGetAuthApi<PaymentDeviceFeesDtoIn>(managementDeviceFeesUrl(month));
  const charges = data?.charges ?? [];
  const months = data?.months ?? [];
  const shownMonth = month ?? charges[0]?.chargeMonth?.slice(0, 10) ?? months[0] ?? null;
  const total = sumFeeCharges(charges.filter((charge) => charge.status !== PaymentDeviceFeeChargeStatus.Skipped));

  const retry = async (charge: PaymentDeviceFeeChargeDtoIn) => {
    setRetryError(null);
    setRetryingId(charge.id);
    try {
      await api.post<PaymentDeviceFeeChargeDtoIn, object>(`${MANAGEMENT_DEVICE_FEES_URL}/${charge.id}/retry`, {});
    } catch (err) {
      setRetryError(paymentDeviceErrorMessage(err, t, 'paymentDevices.fees.retryFailed'));
    } finally {
      setRetryingId(null);
    }
  };

  const columns: ColumnDef<PaymentDeviceFeeChargeDtoIn>[] = [
    {
      id: 'fund',
      header: Col(t('paymentDevices.columns.fund')),
      accessorKey: 'fundId',
      enableSorting: true,
      cell: ({ row }) => <span className="truncate">{row.original.fundId} | {row.original.fundName ?? ''}</span>,
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'device',
      header: Col(t('paymentDevices.columns.device')),
      accessorKey: 'deviceNumber',
      enableSorting: true,
      cell: ({ row }) => <bdi className="truncate">{row.original.deviceNumber}</bdi>,
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'businessDays',
      header: Col(t('paymentDevices.fees.columns.businessDays')),
      accessorKey: 'businessDays',
      enableSorting: true,
      meta: { className: 'min-w-[90px]' },
    },
    {
      id: 'amount',
      header: Col(t('paymentDevices.fees.columns.amount')),
      accessorKey: 'amount',
      enableSorting: true,
      cell: ({ row }) => <span className="font-semibold">{formatFeeAmount(row.original.amount)}</span>,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'status',
      header: Col(t('paymentDevices.columns.status')),
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row }) => {
        const charge = row.original;
        return (
          <span className="flex flex-col items-start gap-0.5 min-w-0">
            <Pill tone={feeChargeTone(charge.status)} small>{t(feeChargeStatusKey(charge.status))}</Pill>
            {charge.lastError && charge.status === PaymentDeviceFeeChargeStatus.Failed && (
              <span className="body-sm-regular text-[#a12626] truncate max-w-full" title={charge.lastError}>
                {t('paymentDevices.fees.attempts', { count: charge.attempts })} · {charge.lastError}
              </span>
            )}
          </span>
        );
      },
      meta: { className: 'min-w-[180px]' },
    },
    {
      id: 'aptMovementId',
      header: Col(t('paymentDevices.fees.columns.aptMovement')),
      accessorKey: 'aptMovementId',
      enableSorting: true,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span>{row.original.aptMovementId ?? '—'}</span>
          {row.original.writtenAt && <span className="body-sm-regular text-(--font-body-3)">{formatTimestamp(row.original.writtenAt)}</span>}
        </span>
      ),
      meta: { className: 'min-w-[130px]' },
    },
    {
      id: 'actions',
      header: Col(t('paymentDevices.columns.actions')),
      enableSorting: false,
      cell: ({ row }) => (row.original.status === PaymentDeviceFeeChargeStatus.Failed ? (
        <Button
          variant="outline"
          disabled={retryingId !== null}
          onClick={(e) => { e.stopPropagation(); retry(row.original); }}
        >
          {retryingId === row.original.id ? t('paymentDevices.fees.retrying') : t('paymentDevices.fees.retry')}
        </Button>
      ) : null),
      meta: { className: 'w-[120px] min-w-[120px] max-w-[120px]', lockLayout: true },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {data && !data.writeToApt && (
        <div role="note" className="rounded-lg bg-(--surface-secondary-low) text-(--font-body-5) px-4 py-3 text-sm">
          {t('paymentDevices.fees.simulationBanner')}
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <label className="flex flex-col gap-1 text-sm text-(--font-body-2)">
          {t('paymentDevices.fees.month')}
          <select
            className={uiStyle['input-style']}
            value={shownMonth ?? ''}
            onChange={(e) => setMonth(e.target.value || null)}
            disabled={months.length === 0}
          >
            {months.length === 0 && <option value="">{t('paymentDevices.fees.noMonths')}</option>}
            {months.map((value) => (
              <option key={value} value={value.slice(0, 10)}>{formatFeeMonth(value)}</option>
            ))}
          </select>
        </label>
        <div className="flex flex-col items-end gap-0.5 text-sm">
          <span className="text-(--font-body-2)">{t('paymentDevices.fees.monthTotal', { month: formatFeeMonth(shownMonth) })}</span>
          <span className="text-xl font-bold text-(--font-primary)">{formatFeeAmount(total)}</span>
          <span className="text-(--font-body-3)">{t('paymentDevices.fees.chargesCount', { count: charges.length })}</span>
        </div>
      </div>

      {retryError && <p role="alert" className="m-0 text-sm text-[#a12626]">{retryError}</p>}

      <DataTable
        title={t('paymentDevices.fees.tableTitle')}
        data={charges}
        isLoading={isLoading}
        columns={columns}
        filters={<SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="paymentDevices.fees.search" />}
        emptyStateContent={{ title: t('paymentDevices.fees.empty.title'), description: t('paymentDevices.fees.empty.description') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={chargeSearch}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="paymentDeviceFees"
        twoLineRows
        getRowId={(charge) => String(charge.id)}
      />
    </div>
  );
};

export default FeesTab;
